import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { HIRING_MODES, INTERVIEW_TYPES, EMPLOYMENT_TYPES, INDIAN_STATES, STATUS_COLORS } from '../utils/constants';
import { getCorporateBranches } from '../utils/api';
import corporateSelector from 'modules/Auth/selectors';

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  height: 2rem;
  width: auto;
`;

const HeaderTitle = styled.div``;

const Title = styled.h1`
  font-weight: bold;
  color: #111827;
  font-size: 1.125rem;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  color: #374151;
`;

const LogoutButton = styled.button`
  font-size: 0.875rem;
  color: #dc2626;
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Content = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
`;

const Message = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 0.375rem;
  color: #047857;
  font-size: 0.875rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
`;

const StatLabel = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const StatValue = styled.p`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.color || '#111827'};
  margin: 0.25rem 0;
`;

const StatSubtext = styled.p`
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0.25rem 0 0 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: bold;
  color: #111827;
  margin: 0;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.variant === 'secondary' ? 'white' : '#2563eb'};
  color: ${props => props.variant === 'secondary' ? '#374151' : 'white'};
  border: ${props => props.variant === 'secondary' ? '1px solid #d1d5db' : 'none'};
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  &:hover {
    background: ${props => props.variant === 'secondary' ? '#f9fafb' : '#1d4ed8'};
  }
  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  font-size: 0.875rem;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Th = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
`;

const Tbody = styled.tbody`
  & tr {
    border-bottom: 1px solid #f3f4f6;
  }
  & tr:hover {
    background: #f9fafb;
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  color: ${props => props.color || '#374151'};
  font-weight: ${props => props.bold ? '500' : 'normal'};
  font-size: ${props => props.small ? '0.875rem' : '0.875rem'};
  white-space: ${props => props.nowrap ? 'nowrap' : 'normal'};
  max-width: ${props => props.maxWidth || 'auto'};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'Open') return '#d1fae5';
    if (props.status === 'In Progress') return '#fef3c7';
    if (props.status === 'Closed') return '#f3f4f6';
    if (props.status === 'Cancelled') return '#fee2e2';
    return '#f3f4f6';
  }};
  color: ${props => {
    if (props.status === 'Open') return '#065f46';
    if (props.status === 'In Progress') return '#92400e';
    if (props.status === 'Closed') return '#4b5563';
    if (props.status === 'Cancelled') return '#991b1b';
    return '#4b5563';
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  font-size: 0.75rem;
  color: ${props => props.color || '#2563eb'};
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: ${props => props.alignTop ? 'flex-start' : 'center'};
  justify-content: center;
  z-index: 50;
  overflow-y: auto;
  padding: ${props => props.alignTop ? '2rem 0' : '0'};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: ${props => props.maxWidth || '48rem'};
  margin: ${props => props.alignTop ? '0 1rem' : '1rem'};
`;

const ModalHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  color: #9ca3af;
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  &:hover {
    color: #4b5563;
  }
`;

const Form = styled.form`
  padding: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    ring: 2px;
    ring-color: #2563eb;
  }
  &:read-only {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    ring: 2px;
    ring-color: #2563eb;
  }
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  resize: vertical;
  &:focus {
    outline: none;
    ring: 2px;
    ring-color: #2563eb;
  }
  &:read-only {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const InfoBox = styled.div`
  padding: 0.75rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 0.375rem;
  color: #92400e;
  font-size: 0.75rem;
  margin-bottom: 1rem;
`;

const CheckboxList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  max-height: ${props => props.maxHeight || '10rem'};
  overflow-y: auto;
  padding: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  background: ${props => props.checked ? '#dbeafe' : 'transparent'};
  color: ${props => props.checked ? '#1e40af' : '#374151'};
  &:hover {
    background: ${props => props.checked ? '#dbeafe' : '#f9fafb'};
  }
`;

const Checkbox = styled.input`
  width: 0.875rem;
  height: 0.875rem;
  color: #2563eb;
  border-radius: 0.25rem;
`;

const CityGrid = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
`;

const CityGridHeader = styled.div`
  background: #f9fafb;
  padding: 0.5rem 0.75rem;
  display: grid;
  grid-template-columns: 1fr 5fr 3fr 3fr;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  border-bottom: 1px solid #e5e7eb;
`;

const CityGridBody = styled.div`
  max-height: 12rem;
  overflow-y: auto;
`;

const CityGridRow = styled.div`
  padding: 0.5rem 0.75rem;
  display: grid;
  grid-template-columns: 1fr 5fr 3fr 3fr;
  gap: 0.5rem;
  align-items: center;
  background: ${props => props.selected ? '#eff6ff' : 'transparent'};
  &:hover {
    background: ${props => props.selected ? '#eff6ff' : '#f9fafb'};
  }
  border-bottom: 1px solid #f3f4f6;
`;

const SmallInput = styled.input`
  width: 70px;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${props => props.borderColor || '#d1d5db'};
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const ValidationError = styled.div`
  font-size: 0.7rem;
  color: #dc2626;
  margin-top: 0.25rem;
  white-space: nowrap;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const PaginationInfo = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PageButton = styled.button`
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
  border: 1px solid ${props => props.active ? '#2563eb' : '#d1d5db'};
  background: ${props => props.active ? '#2563eb' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-radius: 0.25rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  &:hover:not(:disabled) {
    background: ${props => props.active ? '#1d4ed8' : '#f3f4f6'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  padding-top: 0.5rem;
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
`;

const Spinner = styled.div`
  width: 2rem;
  height: 2rem;
  border: 4px solid #2563eb;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EMPTY_FORM = {
  company_name: '',
  roleType: '',
  hiringMode: 'Campus',
  interviewType: 'Face to Face',
  employmentType: 'Full Time',
  maleCount: 0,
  femaleCount: 0,
  expectedJoiningDate: '',
  min_qualification: '',
  additional_remarks: '',
};

const Dashboard = ({
  userData,
  mandatesList,
  loading,
  getMandatesList,
  createNewMandate,
  updateExistingMandate,
  deleteExistingMandate,
  updateMandateCounts: updateMandateCountsAction,
  mandateMetrics,
  getMandateMetrics,
}) => {
  const corporateId = useSelector(state => corporateSelector.getCorporateId(state));
  const corporateData = useSelector(state => corporateSelector.getCorporateData(state));
  const mandatesResponse = useSelector(state => state?.mandates?.mandatesList);
  
  const mandates = mandatesResponse?.result || mandatesList || [];
  console.log('Mandates from selector:', mandatesList);
  console.log('Mandates array:', mandates);
  const [entities, setEntities] = useState([]);
  const [allPlants, setAllPlants] = useState([]);
  const [roleTypes, setRoleTypes] = useState([{ id: 1, name: "Industrial Trainee" }, { id: 2, name: "Operating Engineer Trainee" }]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCities, setSelectedCities] = useState([]);
  const [cityCounts, setCityCounts] = useState({});
  const [selectedStates, setSelectedStates] = useState([]);
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [msg, setMsg] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingCounts, setEditingCounts] = useState({});
  const [countErrors, setCountErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const totalCount = mandatesResponse?.count ?? mandates.length;
  const totalPages = mandatesResponse?.totalPage
    ? parseInt(mandatesResponse.totalPage) || 1
    : Math.max(1, Math.ceil(totalCount / rowsPerPage));

  useEffect(() => {
    setCurrentPage(prev => {
      return prev > totalPages ? totalPages : prev;
    });
  }, [totalPages]);

  useEffect(() => {
    getMandateMetrics();
  }, [corporateId]);

  useEffect(() => {
    getMandatesList({ pageNo: currentPage - 1, pageLimit: rowsPerPage });

    // Fetch corporate branches/locations if corporateId is available
    if (corporateId) {
      getCorporateBranches(corporateId, { pageLimit: 100 })
        .then(response => {
          console.log('Branches API response:', response);
          if (response.success || response.message === 'Success') {
            // The branches are in response.data.list
            const branches = response.data?.list || [];
            console.log('Setting branches:', branches);
            setAllPlants(branches);
          } else {
            console.warn('Failed to fetch branches:', response.message);
          }
        })
        .catch(error => {
          console.error('Error fetching branches:', error);
        });
    }
  }, [corporateId, currentPage]);

  const showMsg = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(''), 3000);
  };

  const totalHeadcount = (parseInt(form.maleCount) || 0) + (parseInt(form.femaleCount) || 0);

  const openNew = () => {
    setForm({
      ...EMPTY_FORM,
      company_name: userData?.company_name || '',
    });
    setSelectedCities([]);
    setCityCounts({});
    setSelectedStates([]);
    setEditId(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = async (m) => {
    setEditId(m.id);
    setFormError('');
    setShowForm(true);

    let mandate = m;
    try {
      const full = await getMandate(m.id);
      if (full.message === 'Success' && full.data) {
        mandate = full.data;
      }
    } catch (err) {
      console.error('Failed to fetch mandate details:', err);
    }

    const joiningDate = mandate.expectedJoiningDate || mandate.expected_joining_date || '';
    const formattedDate = joiningDate ? joiningDate.toString().slice(0, 10) : '';

    setForm({
      ...EMPTY_FORM,
      roleType: mandate.roleType || '',
      hiringMode: mandate.hiringMode || '',
      interviewType: mandate.interviewType || '',
      employmentType: mandate.employmentType || '',
      maleCount: mandate.maleCount || 0,
      femaleCount: mandate.femaleCount || 0,
      expectedJoiningDate: formattedDate,
      additional_remarks: mandate.additionalRemarks || mandate.additional_remarks || '',
    });

    // Restore selected plant
    if (mandate.plantId) {
      setSelectedCities([mandate.plantId]);
    } else if (allPlants.length > 0 && mandate.plantLocation) {
      const matchedPlant = allPlants.find(p =>
        (p.branchName || p.name) === mandate.plantLocation.split(' - ')[0]
      );
      if (matchedPlant) setSelectedCities([matchedPlant.id]);
    }

    // Restore selected states
    const states = mandate.preferredStates;
    if (Array.isArray(states) && states.length > 0) {
      setSelectedStates(states.map(s => typeof s === 'string' ? s : s.name));
    } else {
      setSelectedStates([]);
    }
  };

  const toggleCity = (id) => {
    setSelectedCities(prev => {
      if (prev.includes(id)) {
        const next = prev.filter(c => c !== id);
        setCityCounts(cc => {
          const n = { ...cc };
          delete n[id];
          return n;
        });
        return next;
      }
      setCityCounts(cc => ({ ...cc, [id]: { male_count: 0, female_count: 0 } }));
      return [...prev, id];
    });
  };

  const updateCityCount = (plantId, field, value) => {
    setCityCounts(prev => ({ ...prev, [plantId]: { ...prev[plantId], [field]: value } }));
  };

  const getEntityPlants = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.plants || [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (selectedCities.length === 0) return setFormError('Please select a plant/branch');
    if (parseInt(form.maleCount) < 0 || parseInt(form.femaleCount) < 0) return setFormError('Headcount values cannot be negative');
    if (totalHeadcount === 0) return setFormError('Male + Female headcount must be at least 1');

    setSubmitting(true);
    try {
      const selectedPlant = allPlants.find(p => p.id === selectedCities[0]);
      const plantName = selectedPlant?.branchName || selectedPlant?.name || '';
      const plantCity = selectedPlant?.city || '';
      const plantLocationStr = plantCity ? `${plantName} - ${plantCity}` : plantName;

      // Convert selectedStates to array of objects with name and id
      const preferredStatesPayload = selectedStates.map(stateName => ({
        name: stateName,
        id: ''
      }));

      const payload = {
        roleType: form.roleType,
        hiringMode: form.hiringMode,
        interviewType: form.interviewType,
        employmentType: form.employmentType,
        corporateId: corporateId,
        maleCount: parseInt(form.maleCount) || 0,
        femaleCount: parseInt(form.femaleCount) || 0,
        headcount: totalHeadcount,
        plantLocation: plantLocationStr,
        preferredStates: preferredStatesPayload,
        expectedJoiningDate: form.expectedJoiningDate || undefined,
        additionalRemarks: form.additional_remarks || undefined,
      };
      if (editId) {
        await updateExistingMandate(editId, payload);
      } else {
        await createNewMandate(payload);
      }
      setShowForm(false);
    } catch (err) {
      setFormError(err.message || 'Error saving mandate');
    }
    setSubmitting(false);
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    await deleteExistingMandate(cancelId);
    setCancelId(null);
    setCancelReason('');
  };

  const f = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const formPlants = allPlants;

  const totalHC = mandateMetrics?.totalHeadcount ?? 0;
  const totalMale = mandateMetrics?.totalMale ?? 0;
  const totalFemale = mandateMetrics?.totalFemale ?? 0;
  const totalMandateCount = mandateMetrics?.totalMandateCount ?? 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          {corporateData?.logo && <Logo src={corporateData.logo} alt={corporateData?.name || ''} onError={e => { e.target.style.display = 'none'; }} />}
          <HeaderTitle>
            <Title>{corporateData?.name || 'Hiring Mandate'} Hiring Mandate</Title>
            <Subtitle>
              {userData?.role?.replace('_', ' ').toUpperCase()}
              {userData?.company_name ? ` — ${userData.company_name}` : ''}
            </Subtitle>
          </HeaderTitle>
        </HeaderLeft>
        <HeaderRight>
          <UserName>{userData?.full_name}</UserName>
        </HeaderRight>
      </Header>

      <Content>
        {msg && <Message>{msg}</Message>}

        <StatsGrid>
          <StatCard>
            <StatLabel>Total Headcount</StatLabel>
            <StatValue>{totalHC}</StatValue>
            <StatSubtext>{totalMandateCount} Mandates</StatSubtext>
          </StatCard>
          <StatCard>
            <StatLabel>Male</StatLabel>
            <StatValue color="#2563eb">{totalMale}</StatValue>
            <StatSubtext>{totalHC > 0 ? Math.round(totalMale / totalHC * 100) : 0}% of total</StatSubtext>
          </StatCard>
          <StatCard>
            <StatLabel>Female</StatLabel>
            <StatValue color="#db2777">{totalFemale}</StatValue>
            <StatSubtext>{totalHC > 0 ? Math.round(totalFemale / totalHC * 100) : 0}% of total</StatSubtext>
          </StatCard>
        </StatsGrid>

        <SectionHeader>
          <SectionTitle>Hiring Mandates</SectionTitle>
          <Button onClick={openNew}>+ New Mandate</Button>
        </SectionHeader>

        {loading ? (
          <LoadingSpinner>
            <Spinner />
          </LoadingSpinner>
        ) : mandates.length === 0 ? (
          <EmptyState>No mandates found. Create your first one!</EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <tr>
                  <Th>Date</Th>
                  <Th style={{ width: '220px', whiteSpace: 'nowrap' }}>Role Type</Th>
                  <Th>Plant / City</Th>
                  <Th>City</Th>
                  <Th>Male</Th>
                  <Th>Female</Th>
                  <Th>Total HC</Th>
                  <Th>Joined</Th>
                  <Th>Actions</Th>
                </tr>
              </Thead>
              <Tbody>
                {mandates.map(m => {
                  const counts = editingCounts[m.id];
                  const isEditingCount = !!counts;
                  const error = countErrors[m.id];
                  return (
                    <tr key={m.id}>
                      <Td small color="#9ca3af" nowrap>
                        {new Date(m.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Td>
                      <Td small>{m.roleType}</Td>
                      <Td small title={m.plantNames || m.plantLocation}>
                        {(() => {
                          const v = m.plantNames || m.plantLocation;
                          return v && v.length > 10 ? v.slice(0, 15) + '…' : v;
                        })()}
                      </Td>
                      <Td small title={m.plantLocation}>
                        {(() => {
                          const location = typeof m.plantLocation === 'string' ? m.plantLocation : ''
                          const parts = location.split(' - ').length > 1 ? location.split(' - ') : location.split('-')
                          const city = (parts[1] || '').trim()
                          return city && city.length > 10 ? city.slice(0, 15) + '…' : city
                        })()}
                      </Td>
                      <Td color="#2563eb" bold>
                        {isEditingCount ? (
                          <SmallInput
                            type="number"
                            min={0}
                            value={counts.maleCount}
                            onChange={e => {
                              setEditingCounts(prev => ({ ...prev, [m.id]: { ...prev[m.id], maleCount: Math.max(0, parseInt(e.target.value) || 0) } }));
                              setCountErrors(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                            }}
                            borderColor="#60a5fa"
                          />
                        ) : (m.maleCount || 0)}
                      </Td>
                      <Td color="#db2777" bold>
                        {isEditingCount ? (
                          <SmallInput
                            type="number"
                            min={0}
                            value={counts.femaleCount}
                            onChange={e => {
                              setEditingCounts(prev => ({ ...prev, [m.id]: { ...prev[m.id], femaleCount: Math.max(0, parseInt(e.target.value) || 0) } }));
                              setCountErrors(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                            }}
                            borderColor="#f9a8d4"
                          />
                        ) : (m.femaleCount || 0)}
                      </Td>
                      <Td bold>
                        {isEditingCount ? (counts.maleCount + counts.femaleCount) : m.headcount}
                      </Td>
                      <Td color="#059669" bold>
                        {isEditingCount ? (
                          <div>
                            <SmallInput
                              type="number"
                              min={0}
                              value={counts.joinedCount}
                              onChange={e => {
                                setEditingCounts(prev => ({ ...prev, [m.id]: { ...prev[m.id], joinedCount: Math.max(0, parseInt(e.target.value) || 0) } }));
                                setCountErrors(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                              }}
                              borderColor={error ? '#dc2626' : '#34d399'}
                            />
                            {error && <ValidationError>{error}</ValidationError>}
                          </div>
                        ) : m.joinedCount}
                      </Td>
                      <Td>
                        {m.status !== 'Cancelled' && m.status !== 'Closed' && (
                          <ActionButtons>
                            {isEditingCount ? (
                              <>
                                <ActionButton
                                  color="#059669"
                                  onClick={async () => {
                                    const newTotal = counts.maleCount + counts.femaleCount;
                                    if (counts.joinedCount > newTotal) {
                                      setCountErrors(prev => ({ ...prev, [m.id]: `Joined cannot exceed Total HC` }));
                                      return;
                                    }
                                    await updateMandateCountsAction(m.id, {
                                      headcount: newTotal,
                                      joinedCount: counts.joinedCount,
                                      declinedCount: counts.declinedCount,
                                      maleCount: counts.maleCount,
                                      femaleCount: counts.femaleCount
                                    });
                                    setEditingCounts(prev => {
                                      const n = { ...prev };
                                      delete n[m.id];
                                      return n;
                                    });
                                    setCountErrors(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                                  }}
                                >
                                  Save
                                </ActionButton>
                                <ActionButton
                                  color="#9ca3af"
                                  onClick={() => {
                                    setEditingCounts(prev => {
                                      const n = { ...prev };
                                      delete n[m.id];
                                      return n;
                                    });
                                    setCountErrors(prev => { const n = { ...prev }; delete n[m.id]; return n; });
                                  }}
                                >
                                  ✕
                                </ActionButton>
                              </>
                            ) : (
                              <ActionButton
                                color="#f97316"
                                onClick={() => setEditingCounts(prev => ({
                                  ...prev,
                                  [m.id]: {
                                    maleCount: m.maleCount || 0,
                                    femaleCount: m.femaleCount || 0,
                                    joinedCount: m.joinedCount || 0,
                                    declinedCount: m.declinedCount || 0
                                  }
                                }))}
                              >
                                HC/DC
                              </ActionButton>
                            )}
                            <ActionButton onClick={() => openEdit(m)}>Edit</ActionButton>
                          </ActionButtons>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </Tbody>
            </Table>
            {(() => {
              const startItem = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1
              const endItem = Math.min(currentPage * rowsPerPage, totalCount)

              return (
                <PaginationContainer>
                  <PaginationInfo>
                    Showing {startItem}–{endItem} of {totalCount} mandates
                  </PaginationInfo>
                  <PaginationButtons>
                    <PageButton
                      disabled={currentPage === 1 || totalPages === 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    >
                      Prev
                    </PageButton>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <PageButton
                        key={page}
                        active={page === currentPage}
                        disabled={totalPages === 1}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </PageButton>
                    ))}
                    <PageButton
                      disabled={currentPage === totalPages || totalPages === 1}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </PageButton>
                  </PaginationButtons>
                </PaginationContainer>
              )
            })()}
          </TableContainer>
        )}
      </Content>

      {showForm && (
        <Modal alignTop>
          <ModalContent maxWidth="48rem" alignTop>
            <ModalHeader>
              <ModalTitle>{editId ? 'Edit Mandate' : 'New Hiring Mandate'}</ModalTitle>
              <CloseButton onClick={() => setShowForm(false)}>&times;</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              {editId && (
                <InfoBox>
                  ✏️ You can edit <strong>Expected Joining Date</strong>, <strong>Plant / City</strong>, and <strong>Headcount</strong>. Other fields are shown for reference.
                </InfoBox>
              )}
              <FormGrid>
                <FormGroup>
                  <Label>Role Type *</Label>
                  {editId ? (
                    <Input value={form.roleType} readOnly />
                  ) : (
                    <Select value={form.roleType} onChange={f('roleType')} required>
                      <option value="">Select role type</option>
                      {roleTypes.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </Select>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Interview Mode *</Label>
                  {editId ? (
                    <Input value={form.hiringMode} readOnly />
                  ) : (
                    <Select value={form.hiringMode} onChange={f('hiringMode')}>
                      {HIRING_MODES.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Interview Type *</Label>
                  {editId ? (
                    <Input value={form.interviewType} readOnly />
                  ) : (
                    <Select value={form.interviewType} onChange={f('interviewType')}>
                      {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Employment Type *</Label>
                  {editId ? (
                    <Input value={form.employmentType} readOnly />
                  ) : (
                    <Select value={form.employmentType} onChange={f('employmentType')}>
                      {EMPLOYMENT_TYPES.map(e => <option key={e} value={e}>{e}</option>)}
                    </Select>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Male Headcount *</Label>
                  <Input type="number" min={0} value={form.maleCount} onChange={f('maleCount')} required />
                </FormGroup>

                <FormGroup>
                  <Label>Female Headcount *</Label>
                  <Input type="number" min={0} value={form.femaleCount} onChange={f('femaleCount')} required />
                </FormGroup>

                <FormGroup fullWidth>
                  <Label>Total Headcount (auto)</Label>
                  <Input
                    value={`${totalHeadcount} (Male + Female)`}
                    readOnly
                    style={{ background: '#eff6ff', color: '#1e40af', fontWeight: '600' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Expected Joining Date {editId && <span style={{ color: '#2563eb' }}>(editable)</span>}</Label>
                  <Input type="date" value={form.expectedJoiningDate} onChange={f('expectedJoiningDate')} min={new Date().toISOString().split('T')[0]} />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>Plant / City * {formPlants.length > 0 && <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '0.25rem' }}>({formPlants.length} location{formPlants.length !== 1 ? 's' : ''} available)</span>}</Label>
                {formPlants.length === 0 ? (
                  <div style={{ padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.375rem', background: '#f9fafb', color: '#6b7280', fontSize: '0.875rem' }}>
                    No locations configured. Please add locations in Settings → Location first.
                  </div>
                ) : (
                  <Select 
                    value={selectedCities[0] || ''} 
                    onChange={(e) => {
                      const plantId = e.target.value;
                      if (plantId) {
                        setSelectedCities([plantId]);
                      } else {
                        setSelectedCities([]);
                      }
                    }} 
                    required
                  >
                    <option value="">Select plant/branch location</option>
                    {formPlants.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.branchName || p.name}{p.city ? ` - ${p.city}` : ''}{p.state ? `, ${p.state}` : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </FormGroup>

              <FormGroup style={{ marginBottom: '1rem' }}>
                <Label>Preferred States</Label>
                <CheckboxList maxHeight="10rem">
                  {INDIAN_STATES.map(stateName => {
                    const checked = selectedStates.includes(stateName);
                    return (
                      <CheckboxLabel key={stateName} checked={checked}>
                        <Checkbox
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelectedStates(prev => prev.includes(stateName) ? prev.filter(x => x !== stateName) : [...prev, stateName])}
                          disabled={!!editId}
                        />
                        {stateName}
                      </CheckboxLabel>
                    );
                  })}
                </CheckboxList>
                {selectedStates.length > 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#2563eb', marginTop: '0.25rem' }}>
                    {selectedStates.length} state(s) selected: {selectedStates.join(', ')}
                  </p>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Additional Remarks</Label>
                <TextArea
                  value={form.additional_remarks || ''}
                  onChange={editId ? undefined : f('additional_remarks')}
                  readOnly={!!editId}
                  rows={2}
                />
              </FormGroup>

              {formError && (
                <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                  {formError}
                </div>
              )}

              <ButtonGroup>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : editId ? 'Update Mandate' : 'Submit Mandate'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {cancelId && (
        <Modal>
          <ModalContent maxWidth="28rem">
            <ModalHeader>
              <ModalTitle>Cancel Mandate</ModalTitle>
              <CloseButton onClick={() => setCancelId(null)}>&times;</CloseButton>
            </ModalHeader>
            <div style={{ padding: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '1rem' }}>
                Are you sure you want to cancel this mandate?
              </p>
              <TextArea
                placeholder="Reason for cancellation (optional)"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={3}
                style={{ marginBottom: '1rem' }}
              />
              <ButtonGroup>
                <Button onClick={handleCancel} style={{ background: '#dc2626' }}>Yes, Cancel</Button>
                <Button variant="secondary" onClick={() => setCancelId(null)}>Go Back</Button>
              </ButtonGroup>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Dashboard;
